import { createClient } from '@supabase/supabase-js';
import type { Member } from '../types/member';
import type { Database, TablesInsert } from '../types/database.types';

// Create a client with the anonymous key for public operations
export const supabase = createClient<Database>(
  import.meta.env.VITE_SUPABASE_URL || '',
  import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  {
    auth: {
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true
    }
  }
);

// Authentication helpers
export const sendMagicLink = async (email: string, captchaToken?: string, createUser = false) => {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/account`,
      shouldCreateUser: createUser,
      captchaToken,
    },
  });

  return { data, error };
};

export const signOut = async () => {
  return await supabase.auth.signOut();
};

export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  return { user: data.user, error };
};

// Member functions
export const getMemberByEmail = async (email: string) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { member: null, error: new Error('No active session') };
  }

  const normalizedEmail = email.trim().toLowerCase();

  // Check primary email first
  const { data: primaryMatch, error: primaryError } = await supabase
    .from('members')
    .select('*')
    .eq('email', normalizedEmail)
    .maybeSingle();

  if (primaryError) return { member: null, error: primaryError };
  if (primaryMatch) return { member: primaryMatch, error: null };

  // Fall back to alternate_emails array search
  const { data: alternateMatch, error: alternateError } = await supabase
    .from('members')
    .select('*')
    .contains('alternate_emails', [normalizedEmail])
    .maybeSingle();

  return { member: alternateMatch ?? null, error: alternateError ?? null };
};

export const updateMember = async (id: string, updates: Partial<Member>) => {
  const { data, error } = await supabase
    .from('members')
    .update(updates)
    .eq('id', id)
    .select();
  
  return { member: data?.[0], error };
};

export const registerMember = async (memberData: TablesInsert<'members'>) => {
  const { data, error } = await supabase
    .from('members')
    .insert([memberData])
    .select();

  return { member: data?.[0], error };
};

// Interest functions
export const getInterestCategories = async () => {
  try {
    // First fetch all categories
    const { data: categories, error: categoriesError } = await supabase
      .from('interest_categories')
      .select('*')
      .order('display_order');

    if (categoriesError) throw categoriesError;

    // Then fetch all interests
    const { data: interests, error: interestsError } = await supabase
      .from('interests')
      .select('*')
      .order('name');

    if (interestsError) throw interestsError;

    // Combine the data
    const categoriesWithInterests = categories.map(category => ({
      ...category,
      interests: interests.filter(interest => interest.category_id === category.id)
    }));

    return { categories: categoriesWithInterests, error: null };
  } catch (error) {
    console.error('Error fetching interest categories:', error);
    return { categories: [], error };
  }
};

export const getMemberInterests = async (memberId: string) => {
  const { data, error } = await supabase
    .from('member_interests')
    .select(`
      interest_id,
      interests (
        id,
        name,
        category:interest_categories (
          id,
          name
        )
      )
    `)
    .eq('member_id', memberId);
  
  const interests = (data ?? [])
    .map(i => i.interests)
    .filter((interest): interest is NonNullable<typeof interest> => interest !== null)
    .map(interest => ({
      id: interest.id,
      name: interest.name,
      category: interest.category,
    }));

  return { interests, error };
};

export const updateMemberInterests = async (memberId: string, interestIds: string[]) => {
  // First delete existing interests
  await supabase
    .from('member_interests')
    .delete()
    .eq('member_id', memberId);
  
  // Then add new ones
  if (interestIds.length > 0) {
    const interestData = interestIds.map(id => ({
      member_id: memberId,
      interest_id: id
    }));
    
    const { data, error } = await supabase
      .from('member_interests')
      .insert(interestData);
    
    return { data, error };
  }
  
  return { data: null, error: null };
};

// Volunteer hours functions
export const logVolunteerHours = async (data: any) => {
  const { data: result, error } = await supabase
    .from('volunteer_hours')
    .insert([data])
    .select();
  
  return { record: result?.[0], error };
};

export const getMemberVolunteerHours = async (memberId: string) => {
  const { data, error } = await supabase
    .from('volunteer_hours')
    .select('*')
    .eq('member_id', memberId)
    .order('date', { ascending: false });
  
  return { hours: data, error };
};

// Meeting attendance functions
export const recordAttendance = async (data: any) => {
  const { data: result, error } = await supabase
    .from('meeting_attendance')
    .insert([data])
    .select();
  
  return { record: result?.[0], error };
};

export const getMeetings = async () => {
  const { data, error } = await supabase
    .from('meetings')
    .select('*')
    .order('date', { ascending: false });
  
  return { meetings: data, error };
};

export const getMemberAttendance = async (memberId: string) => {
  const { data, error } = await supabase
    .from('meeting_attendance')
    .select('*, meetings(*)')
    .eq('member_id', memberId)
    .order('created_at', { ascending: false });
  
  return { attendance: data, error };
};

// Payment functions
export const getMemberPayments = async (memberId: string) => {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('member_id', memberId)
    .order('date', { ascending: false });
  return { payments: data, error };
};